/**
 * AccountOverview component with dark theme styling.
 */

import { Card, CardHeader, CardTitle, CardContent, Spinner } from '@/components/ui';
import { formatCurrency, formatDateTime } from '@/utils/formatters';
import { NlvInput } from './NlvInput';
import type { AccountSummary } from '@/types';

interface AccountOverviewProps {
  summary: AccountSummary | null;
  isLoading?: boolean;
  error?: string | null;
  nlv?: number | null;
  nlvUpdatedAt?: string | null;
  nlvSource?: 'tastytrade' | 'manual' | null;
  nlvSaving?: boolean;
  nlvSaveError?: string | null;
  onNlvSave?: (nlv: number) => Promise<void>;
}

export function AccountOverview({
  summary,
  isLoading = false,
  error = null,
  nlv = null,
  nlvUpdatedAt = null,
  nlvSource = null,
  nlvSaving = false,
  nlvSaveError = null,
  onNlvSave,
}: AccountOverviewProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-rose-400">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-dark-500">Select an account to view details</p>
        </CardContent>
      </Card>
    );
  }

  const realizedPnlColor = summary.realized_pnl >= 0 ? 'text-emerald-400' : 'text-rose-400';

  return (
    <Card>
      <CardHeader>
        <CardTitle>{summary.name}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-dark-400">Total Cost Basis</p>
            <p className="text-xl font-bold font-mono tabular-nums text-dark-100">
              {formatCurrency(summary.total_cost_basis)}
            </p>
          </div>
          {onNlvSave && (
            <NlvInput
              nlv={nlv}
              nlvUpdatedAt={nlvUpdatedAt}
              nlvSource={nlvSource}
              isSaving={nlvSaving}
              saveError={nlvSaveError}
              onSave={onNlvSave}
              totalCostBasis={summary.total_cost_basis}
            />
          )}
          <div>
            <p className="text-sm text-dark-400">Realized P&L (YTD)</p>
            <p className={`text-xl font-bold font-mono tabular-nums ${realizedPnlColor}`}>
              {formatCurrency(summary.realized_pnl)}
            </p>
          </div>
          <div>
            <p className="text-sm text-dark-400">Closed Positions (YTD)</p>
            <p className="text-xl font-bold font-mono tabular-nums text-dark-100">
              {summary.position_count}
            </p>
          </div>
        </div>
        <p className="mt-4 text-xs text-dark-500">
          Last updated: {formatDateTime(summary.last_updated)}
        </p>
      </CardContent>
    </Card>
  );
}
